import { AfterViewInit, ChangeDetectorRef, Component, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { SelectionModel } from '@angular/cdk/collections';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { BehaviorSubject, filter, take } from 'rxjs';
import { v4 as uuid } from 'uuid';
import { GuestVerificationInfo } from '../models/verification-info/GuestVerificationInfo';
import { Logger, LogLevel } from '../../../infrastructure/logging/logger';
import { UserContextProvider } from '../../../infrastructure/context/user-context-provider';
import { UserRegion } from '../../../infrastructure/context/UserContext';
import { NavService } from '../../../layout/nav/nav.service';
import { DataTableFilterMessageService, ActiveEventMessage } from '../../../shared/components/data-table/filters/components/data-table-filter-message-service';
import { DataTableFilterPillComponent, DataTableFilterPillState } from '../../../shared/components/data-table/filters/components/data-table-filter-pill/data-table-filter-pill.component';
import { DataTableFilter } from '../../../shared/components/data-table/filters/components/DataTableFilter';
import { SearchService } from '../../../shared/search/search.service';
import { SortParams, SortOrderType } from '../../../shared/search/SearchParams';
import { GuestService } from '../services/guest.service';
import { GuestVerificationResultBuilder } from '../shared/guest-verification-result-builder';
import { GuestFilterStatusComponent } from './components/guest-filter-status/guest-filter-status.component';
import { GuestFilterStateService } from './services/guest-filter-state.service';
import { GuestMessageService } from './services/guest-message.service';
import { DataTableFilterComponent } from '../../../shared/components/data-table/filters/components/data-table-filter/data-table-filter.component';
import { ColorProp, getColor } from '../../../layout/Colors';
import { isStringEmptyOrNull } from '../../../utilities/StringUtility';
import { SafelyResource } from '../../../shared/SafelyResource';
import { NotificationService } from '../../../utilities/PortalNotificationUtility';

@Component({
  selector: 'app-guest-table',
  templateUrl: './guest-table.component.html',
  styleUrls: ['./guest-table.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatIcon,
    MatCardModule,
    DataTableFilterComponent,
    MatFormFieldModule,
    MatInputModule,
    DataTableFilterPillComponent,
    MatTableModule,
    MatChipsModule,
    MatTooltip,
    MatProgressSpinner,
    MatPaginator,
    FormsModule,
    MatButtonModule,
    MatSortModule
  ]
})
export class GuestTableComponent implements OnInit, AfterViewInit {

  public rows = new MatTableDataSource<any>();
  public columns = [];
  public selection = new SelectionModel<any>(true, []);

  VERIFICATION_RESULT_CODE_PASSED = 1;
  VERIFICATION_RESULT_CODE_CONSIDER = 2;
  VERIFICATION_RESULT_CODE_PROCESSING = 3;
  VERIFICATION_RESULT_CODE_NO_VERIFICATION = 4;

  filterDefinitions: DataTableFilter[] = [];

  tableColumns: string[] = [];

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChildren(MatSort) sortList!: QueryList<MatSort>;
  @ViewChild(MatSort) sort: MatSort = new MatSort();

  dataSource = new MatTableDataSource<GuestVerificationInfo>();
  filterPills = new Map<string, DataTableFilterPillState>();

  initialLoad = true;
  loading = new BehaviorSubject<boolean>(false);
  pageSize = 10;
  tableLength = 0;
  searchKeyword = '';
  pageNumber!: number;
  allGuestsPageNumber = 1;
  legendVisible = true;
  returnEmptyDataSource: boolean = false;
  screenGuestEnabled: boolean = false;

  getColor = getColor;
  colorProp = ColorProp;
  resource = SafelyResource.RESERVATION;

  organizationId!: any;
  selectedOrganization!: any;
  selectedOrganizationName: string = '';

  initialKeyword = new BehaviorSubject<string>('');
  initialSort = new BehaviorSubject<Sort | undefined>(undefined);
  initialPageSize = new BehaviorSubject<number>(10);
  initialPageNumber = new BehaviorSubject<number>(1);

  constructor(
    public userContextProvider: UserContextProvider,
    private router: Router,
    private guestService: GuestService,
    private snackbar: MatSnackBar,
    private searchService: SearchService,
    private guestMessageService: GuestMessageService,
    private guestFilterStateService: GuestFilterStateService,
    private filterMessageService: DataTableFilterMessageService,
    private guestVerificationResultBuilder: GuestVerificationResultBuilder,
    private logService: Logger,
    private notificationService: NotificationService,
    private navService: NavService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.userContextProvider.isContextLoaded$.pipe(
      filter(loaded => loaded),
      take(1)
    ).subscribe(_ => {
      this.screenGuestEnabled = false;
      this.searchService.resetQuery();
      this.setupTableColumns();
      this.updateFilterPillTextAfterFilterEvent();

      const storedItemsPerPage = localStorage.getItem('guestsItemsPerPage');
      if (storedItemsPerPage) {
        this.initialPageSize.next(parseInt(storedItemsPerPage, 10));
      } else {
        this.initialPageSize.next(10);
      }

      this.setSearchQuery();
      this.setupPagingState();
      this.setupFilterState();
      this.setupSearchTextState();
      this.setupSortState();
      this.setupTableFilters();
      this.setupTableColumns();
      this.setupFilterEventHandler();

      this.navService.selectedOrg$.subscribe((org: any) => {
        if (org?.organization_id) {
          this.selectedOrganization = org;
          this.organizationId = org.organization_id;
          this.onOrganizationSearchEvent(org.organization_id);
        } else {
          this.selectedOrganization = undefined;
          this.selectedOrganizationName = '';
          this.loadPage();
        }
      });
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;

    this.initialPageSize?.subscribe(pageSize => {
      this.pageSize = pageSize;
      this.paginator.pageSize = pageSize;
      this.cdr.detectChanges();
    });

    this.initialPageNumber?.subscribe(pageNumber => {
      if (pageNumber != 1) {
        this.pageNumber = pageNumber;
        this.paginator.pageIndex = pageNumber;
      } else {
        this.pageNumber = 1;
      }
      this.cdr.detectChanges();
    });

    this.initialKeyword?.subscribe(keyword => {
      this.searchKeyword = keyword;
      this.cdr.detectChanges();
    });

    this.initialSort?.subscribe(sort => {
      if (!sort) {
        return;
      }

      this.setSort(sort);
      this.cdr.detectChanges();
    });
  }

  setSort(sort: Sort) {
    this.sort.sort({ id: sort.active, start: sort.direction, disableClear: false });
  }

  setSearchQuery() {
    if (this.searchService.currentQuery.sortParams?.sortField === 'guestId') {
      if (this.searchService.currentQuery.page > 1) {
        this.searchService.setPage(this.searchService.currentQuery.page);
        this.pageNumber = this.searchService.currentQuery.page;
      } else {
        this.searchService.setPage(1);
        this.pageNumber = 1;
      }
    } else {
      this.searchService.resetQuery();
      this.pageNumber = 1;
    }
    this.searchService.setSort("guestId", -1);
  }

  private setupPagingState() {
    const pageSize = this.guestService.lastSearchRequest?.pageSize;
    if (pageSize) {
      this.searchService.setPageSize(pageSize);
      this.pageSize = pageSize;
    }
  }

  private setupFilterState() {
    if (this.guestService.lastSearchRequest?.filterParams?.find(f => f.fieldName === 'verificationResultCode')) {
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.passed, '1001');
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.consider, '3001');
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.processing, '4001');
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.noVerification, '5001');
    }
  }

  private setupSearchTextState() {
    const freeText = this.guestService.lastSearchRequest?.freeText;
    if (!isStringEmptyOrNull(freeText)) {
      this.addKeywordToQuery(freeText!!);
      this.searchKeyword = freeText!!;
    }
  }

  private setupSortState() {
    const sort = this.guestService.lastSearchRequest?.sortParams;
    if (sort) {
      this.searchService.addSort(sort);
      this.sort?.sort({ id: sort.sortField, start: sort.sortOrder == 1 ? 'asc' : 'desc', disableClear: false });
    } else {
      this.sort?.sort({ id: 'guestId', start: 'desc', disableClear: false });
    }
  }

  setupFilterEventHandler() {
    this.guestMessageService.applyFilterEvent.subscribe(_ => {
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.passed, '1001');
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.consider, '3001');
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.processing, '4001');
      this.updateVerificationResultFilterAfterFilterApplied(this.guestFilterStateService.noVerification, '5001');

      this.loadPage();
    });
  }

  async loadPage() {
    if (this.returnEmptyDataSource) {
      this.updateTableDataSource([], 0);
      return;
    }

    try {
      this.loading.next(true);
      this.dataSource.data = [];

      const { guests, totalRecords } = await this.guestService.searchGuests(this.searchService.currentQuery);
      const mappedGuests = guests.map(guest => this.guestVerificationResultBuilder.buildFromGuest(guest, this.userContextProvider.region));
      this.updateTableDataSource(mappedGuests, totalRecords);
    } catch (e) {
      this.notificationService.showNotification({ message: 'An error occurred while loading Guests page'});
      this.logService.log({ 
        message: `Error while retrieving Guests page`, 
        level: LogLevel.ERROR,
        error: e
      });
    } finally {
      this.loading.next(false);
    }
  }

  setupTableFilters() {
    const screeningResultFilterName = 'Screening Result';
    this.filterDefinitions = [
      { id: uuid(), name: screeningResultFilterName, shortName: screeningResultFilterName, component: GuestFilterStatusComponent }
    ];
  }

  setupTableColumns() {
    switch(this.userContextProvider.region) {
      case UserRegion.NA:
        this.setupNaTableColumns();
        break;
      case UserRegion.EU:
        this.setupEuTableColumns();
        break;
      default:
        this.logService.handleError(`Unknown region ${this.userContextProvider.region}`, `Unknown region ${this.userContextProvider.region}`, `Unknown region ${this.userContextProvider.region}`);
        break;
    }
  }

  setupNaTableColumns() {
    this.tableColumns = [
      'reservationId',
      'reservationReferenceId',
      'organizationId',
      'organizationName',
      'fullName',
      'verifiedDate',
      'arrivalDate',
      'identityResultCode',
      'watchlistResultCode',
      'sexOffenderResultCode',
      'nationalCriminalCode',
      'fraudResultCode',
      'badGuestResultCode',
      'lastEmailSent'
    ];
  }

  setupEuTableColumns() {
    this.tableColumns = [
      'reservationId',
      'reservationReferenceId',
      'organizationId',
      'organizationName',
      'fullName',
      'verifiedDate',
      'identityResultCode',
      'watchlistResultCode', //sanctions list
      'lastEmailSent'
    ];
  }

  updateTableDataSource(guests: GuestVerificationInfo[], totalRecords: number) {
    if (this.selectedOrganization) {
      this.selectedOrganizationName = this.selectedOrganization.organization_name;
    } else {
      this.selectedOrganizationName = '';
    }
    this.dataSource = new MatTableDataSource(guests);
    this.rows = this.dataSource;
    this.dataSource.sortData = (data: any[], _: MatSort) => data;
    this.dataSource.sort = this.sort;
    this.tableLength = totalRecords;
  }

  onPageEvent(event: PageEvent): void {
    this.pageSize = event.pageSize;
    this.pageNumber = event.pageIndex + 1;
    this.searchService.setPageSize(event.pageSize);
    this.searchService.setPage(event.pageIndex + 1);
    this.loadPage();
  }

  onSearchEvent(keyword: string) {
    this.searchKeyword = keyword;
    this.searchService.addTextQuery(keyword);
    this.loadPage();
  }

  private addKeywordToQuery(keyword: string) {
    this.searchService.addTextQuery(keyword);
    this.searchService.setPage(1);
  }

  onOrganizationSearchEvent(organizationId: any) {
    this.searchService.setOrganizationId(organizationId);
    this.searchService.setPage(1);
    this.loadPage();
  }

  onSortEvent(event: any) {
    this.addSortToQuery(event);
    this.loadPage();
  }

  private addSortToQuery(sortParams: SortParams) {
    this.searchService.addSort(this.buildSortParams(sortParams));
  }

  buildSortParams(event: any): SortParams {
    let { active: sortField, direction } = event;

    let sortOrder: SortOrderType;
    if (direction === 'asc') {
      sortOrder = 1;
    } else {
      sortOrder = -1;
    }

    return {
      sortField,
      sortOrder
    }
  }

  onDeleteKeyUp(searchKeyword: any) {
    if (searchKeyword === '') {
      this.onSearchEvent('');
    }
  }

  clearSearch() {
    this.searchService.resetQuery();
    this.searchKeyword = '';
    this.resetPaginator(1, true);
    this.onSearchEvent('');
  }

  onFilterPillCloseEvent(filterId: string) {
    this.filterMessageService.onClearClicked(filterId);
  }

  resetPaginator(pageNumber: number, goToFirstPage: boolean) {
    if (!this.paginator) {
      return;
    }
    
    this.pageNumber = pageNumber;
    if (goToFirstPage) {
      this.paginator.firstPage();
    } else {
      this.paginator.lastPage();
    }
  }

  onGoToPageClick() {
    if (!this.pageNumber) {
      return;
    }

    const max = Math.trunc(this.paginator.length / this.paginator.pageSize);
    if (this.pageNumber < 1) {
      this.resetPaginator(1, true);
      this.displayInvalidPaginatorPageIndexError();
    } else if (this.pageNumber > max) {
      this.resetPaginator(max, false);
      this.displayInvalidPaginatorPageIndexError();
    } else {
      this.paginator.pageIndex = this.pageNumber - 1;
      this.paginator.page.next({
        pageIndex: this.pageNumber - 1,
        pageSize: this.paginator.pageSize,
        length: this.paginator.length
      });
    }
  }

  displayInvalidPaginatorPageIndexError() {
    this.snackbar.open('Invalid page number', 'OK', { duration: 3000 });
  }

  onRowClick(row: any): void {
    this.router.navigate([`/guests/detail/${row['id']}`]);
  }

  onNewGuestClick() {
    this.router.navigate(['/verify-guest']);
  }

  onEquifaxResultsClick() {
    this.router.navigate(['/equifax-lookup']);
  }

  updateVerificationResultFilterAfterFilterApplied(statusEnabled: boolean, statusName: string) {
    const {
      passed,
      consider,
      processing,
      noVerification
    } = this.guestFilterStateService;
    this.returnEmptyDataSource = !passed && !consider && !processing && !noVerification;

    if (statusEnabled) {
      this.updateVerificationResultFilter(statusName);
    } else {
      this.removeVerificationFilterValue(statusName);
    }
  }

  updateVerificationResultFilter(verificationResultCode: string) {
    this.searchService.updateFilter({
      fieldName: 'verificationResultCode',
      value: [verificationResultCode],
      range: false
    });
  }

  removeVerificationFilterValue(verificationResultCode: string) {
    this.searchService.removeValueFromFilter('verificationResultCode', verificationResultCode);
  }

  updateFilterPillTextAfterFilterEvent() {
    this.filterMessageService.activeEvent.subscribe((event: ActiveEventMessage) => {
      if (!this.filterDefinitions) {
        return;
      }

      this.resetPaginator(1, true);
      if (event.active) {
        let pillText = this.filterDefinitions.find(definition => definition.id == event.filterId)?.shortName || '';
        if (event.value) {
          pillText = pillText + " : " + event.value;
        }
        this.filterPills.set(event.filterId, { filterId: event.filterId, text: pillText });
      } else {
        this.filterPills.delete(event.filterId);
      }
      this.cdr.detectChanges();
    });
  }

  toggleLegendVisibility() {
    this.legendVisible = !this.legendVisible;
  }

  getLegendVisibilityToggleText() {
    return this.legendVisible ? 'Hide [-]' : 'Show [+]';
  }

  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.rows.data.length;
    return numSelected === numRows;
  }

  public masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.rows.data.forEach(row => this.selection.select(row));
  }

  public checkboxLabel(row?: any): string {
    return (!row)
      ? `${this.isAllSelected() ? 'select' : 'deselect'} all`
      : `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.position + 1}`;
  }
}